import type { Company } from '../lib/types.ts';
import type { CompanyInfoLabelKey } from '../lib/companyInfo.ts';
import { REQUIRED_COMPANY_INFO_FIELDS, type RequiredCompanyInfoField } from '@l2u/shared/progress';

// Single source of truth for the editable vendor company-info form rendered by
// the questionnaire wizard's inline "Vendor information" step.
//
// IMPORTANT: every field the submit gate requires (REQUIRED_COMPANY_INFO_FIELDS)
// MUST have an entry here. If the gate requires a field the form doesn't render,
// the vendor gets bounced to the company-info step but has no input to fill —
// the exact drift that silently broke "Submit for review" after PR #84 added
// legal entity / address fields to the gate without updating this form.
// companyInfoForm.test.ts guards the two lists against drifting again.

export type CompanyInfoFormValues = {
  abn: string;
  trustee_info: string;
  primary_contact_name: string;
  contact_role: string;
  contact_phone: string;
  contact_email: string;
  employee_count: string;
  legal_entity_name: string;
  address_line: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
};

export type CompanyInfoFieldKey = keyof CompanyInfoFormValues;

export type CompanyInfoFieldConfig = {
  key: CompanyInfoFieldKey;
  // Static label. When labelKey is set the renderer resolves the label via
  // companyInfoLabel(settings, labelKey) instead (admin-customisable text).
  label: string;
  labelKey?: CompanyInfoLabelKey;
  required: boolean;
  // Maps to the <TextInput type> prop; defaults to a plain text input.
  inputType?: 'tel' | 'email' | 'number';
  hint?: string;
};

// Ordered for rendering. trustee_info is the only optional field (only required
// "if applicable", so it is omitted from REQUIRED_COMPANY_INFO_FIELDS).
export const COMPANY_INFO_FORM_FIELDS: CompanyInfoFieldConfig[] = [
  // ABN leads: it drives the live verification + auto-fill of legal entity name,
  // state and postcode, so the user enters it before the fields it populates.
  { key: 'abn', label: 'ABN', required: true },
  {
    key: 'trustee_info',
    label: 'Trustee Name and ACN (if applicable)',
    required: false,
    hint: 'Only required if you operate under a trust.',
  },
  { key: 'legal_entity_name', label: 'Legal entity name', required: true },
  { key: 'address_line', label: 'Address', required: true },
  { key: 'city', label: 'City', required: true },
  { key: 'state', label: 'State', required: true },
  { key: 'postcode', label: 'Postcode', required: true },
  { key: 'country', label: 'Country', required: true },
  {
    key: 'primary_contact_name',
    label: 'Primary contact name',
    labelKey: 'primaryContact',
    required: true,
  },
  { key: 'contact_role', label: 'Contact role/position', required: true },
  { key: 'contact_phone', label: 'Contact phone number', required: true, inputType: 'tel' },
  { key: 'contact_email', label: 'Contact email address', required: true, inputType: 'email' },
  { key: 'employee_count', label: '# of employees', required: true, inputType: 'number' },
];

export function toCompanyInfoFormValues(c: Company): CompanyInfoFormValues {
  return {
    abn: c.abn ?? '',
    trustee_info: c.trustee_info ?? '',
    primary_contact_name: c.primary_contact_name ?? '',
    contact_role: c.contact_role ?? '',
    contact_phone: c.contact_phone ?? '',
    contact_email: c.contact_email ?? '',
    employee_count: c.employee_count === null ? '' : String(c.employee_count),
    legal_entity_name: c.legal_entity_name ?? '',
    address_line: c.address_line ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    postcode: c.postcode ?? '',
    country: c.country ?? '',
  };
}

// Required fields still empty in the form. Derived from the shared submit gate
// so this step can never validate a different set than the gate enforces.
export function companyInfoFormMissing(form: CompanyInfoFormValues): RequiredCompanyInfoField[] {
  return REQUIRED_COMPANY_INFO_FIELDS.filter((field) => {
    const value = form[field];
    return value.trim() === '';
  });
}

// Build the PATCH /api/me payload from the form. Throws on an invalid
// employee_count so the caller can surface the message (mirrors CompanyInfo.tsx).
export function buildCompanyInfoPayload(form: CompanyInfoFormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    abn: form.abn.trim() || null,
    trustee_info: form.trustee_info.trim() || null,
    primary_contact_name: form.primary_contact_name.trim() || null,
    contact_role: form.contact_role.trim() || null,
    contact_phone: form.contact_phone.trim() || null,
    contact_email: form.contact_email.trim() || null,
    legal_entity_name: form.legal_entity_name.trim() || null,
    address_line: form.address_line.trim() || null,
    city: form.city.trim() || null,
    state: form.state.trim() || null,
    postcode: form.postcode.trim() || null,
    country: form.country.trim() || null,
  };
  const empCount = form.employee_count.trim();
  if (empCount === '') {
    payload.employee_count = null;
  } else {
    const n = Number.parseInt(empCount, 10);
    if (!Number.isFinite(n) || n < 0) {
      throw new Error('# of employees must be a non-negative number');
    }
    payload.employee_count = n;
  }
  return payload;
}

/**
 * Whether the questionnaire wizard should render the "Vendor information" step.
 * Shown until the vendor submits for the first time; hidden thereafter so later
 * questionnaires don't re-prompt for company info already on file (the ABN /
 * legal entity are locked post-submission anyway).
 *
 * The `missingCount > 0` clause is a safety net: if a required company-info
 * field is still outstanding (e.g. a field added to the submit gate after the
 * vendor first submitted), the step reappears so they are never stranded behind
 * a disabled Submit with nowhere to enter it. The submit gate itself is
 * visibility-unaware (it reads the missing-field count directly); the safeguard
 * is that this helper forces the step visible whenever company info is missing,
 * so the gate can never block with nowhere to enter data — hence no gate change.
 */
export function companyInfoStepVisible(
  includeStep: boolean,
  hasSubmittedBefore: boolean,
  missingCount: number,
): boolean {
  return includeStep && (!hasSubmittedBefore || missingCount > 0);
}
